// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react'
import {
	onAuthStateChanged,
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	signOut,
	GoogleAuthProvider,
	FacebookAuthProvider,
	signInWithPopup,
	sendPasswordResetEmail,
	updateProfile,
} from 'firebase/auth'
import { auth } from '../firebase'

const AuthContext = createContext(null)

export function useAuth() {
	return useContext(AuthContext)
}

export function AuthProvider({ children }) {
	const [currentUser, setCurrentUser] = useState(null)
	const [loading, setLoading] = useState(true)

	function signup(email, password, firstName, lastName) {
		return createUserWithEmailAndPassword(auth, email, password).then(
			result => {
				// Update profile with name after account creation
				return updateProfile(result.user, {
					displayName: `${firstName} ${lastName}`,
				}).then(() => result)
			},
		)
	}

	function login(email, password) {
		return signInWithEmailAndPassword(auth, email, password)
	}

	function logout() {
		return signOut(auth)
	}

	function loginWithGoogle() {
		const provider = new GoogleAuthProvider()
		return signInWithPopup(auth, provider)
	}

	function loginWithFacebook() {
		const provider = new FacebookAuthProvider()
		return signInWithPopup(auth, provider)
	}

	function signupWithGoogle() {
		// For Google, signup uses the same method as login
		return loginWithGoogle()
	}

	function signupWithFacebook() {
		// For Facebook, signup uses the same method as login
		return loginWithFacebook()
	}

	function resetPassword(email) {
		return sendPasswordResetEmail(auth, email)
	}

	function updateUserProfile(user, data) {
		return updateProfile(user, data)
	}

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, user => {
			setCurrentUser(user)
			setLoading(false)
		})

		return unsubscribe
	}, [])

	const value = {
		currentUser,
		loading,
		signup,
		login,
		logout,
		loginWithGoogle,
		loginWithFacebook,
		signupWithGoogle,
		signupWithFacebook,
		resetPassword,
		updateUserProfile,
	}

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
